import { BoltAmbition, City } from './types.ts'
import { Language, LANGUAGES, REGIONS, Region } from '@boltenergy-be/shared'
import {
  AMBITION_CHART_CITIES,
  AMBITION_GOAL_MILESTONES,
  AMBITION_GOAL_STEP_ABOVE_MILESTONES,
  AMBITION_MAX_BEATEN_CITIES,
  PROVINCES_WITH_CITIES,
  type Province
} from './constants.ts'

/**
 * Returns the province code based on the given postal code
 *
 * @param {number} postalCode
 * @returns {Province|null}
 */
export const getProvinceCode = (postalCode: number): Province | null => {
  // Brussels
  if (postalCode >= 1000 && postalCode <= 1299) return 'Brussels'

  // Flanders
  if (postalCode >= 8000 && postalCode <= 8999) return 'West-Flanders'
  if (postalCode >= 9000 && postalCode <= 9999) return 'East-Flanders'
  if (postalCode >= 2000 && postalCode <= 2999) return 'Antwerp'
  if (postalCode >= 3500 && postalCode <= 3999) return 'Limburg'
  if ((postalCode >= 1500 && postalCode <= 1999) || (postalCode >= 3000 && postalCode <= 3499)) return 'Flemish Brabant'

  // Wallonia
  if (postalCode >= 1300 && postalCode <= 1499) return 'Walloon Brabant'
  if ((postalCode >= 6000 && postalCode <= 6599) || (postalCode >= 7000 && postalCode <= 7999)) return 'Hainaut'
  if (postalCode >= 5000 && postalCode <= 5999) return 'Namur'
  if (postalCode >= 4000 && postalCode <= 4999) return 'Liege'
  if (postalCode >= 6600 && postalCode <= 6999) return 'Luxembourg'

  return null
}

/**
 * Returns the region of the user
 * @param {Province|null} province
 * @param {Language} language
 * @param {boolean} includeBru
 * @returns Region
 */
export const getSelectedRegion = (province: Province | null, language: Language, includeBru?: boolean): Region => {
  const region = PROVINCES_WITH_CITIES.find((prov) => prov.province === province)?.region

  if (!region) {
    return language === LANGUAGES.FR ? REGIONS.wallonia : REGIONS.flanders
  }

  if (!includeBru && region === REGIONS.brussels) {
    return language === LANGUAGES.FR ? REGIONS.wallonia : REGIONS.flanders
  }

  return region
}

/**
 * Returns the first ambition milestone above the given total,
 * or the next step above the last milestone
 *
 * @param {number} total
 * @returns {number}
 */
export const getAmbitionGoal = (total: number): number => {
  const milestone = AMBITION_GOAL_MILESTONES.find((milestone) => milestone > total)
  return milestone ?? (Math.floor(total / AMBITION_GOAL_STEP_ABOVE_MILESTONES) + 1) * AMBITION_GOAL_STEP_ABOVE_MILESTONES
}

/**
 * Returns Bolt's next goal with the cities around Bolt's community size for the ambition chart, or null when Bolt
 * has not beaten any city or has no city left to catch up with.
 * Beaten cities prefer the given province. The first upcoming city is the one closest to the goal, preferring
 * cities of the given province that stay below the milestone after the goal, so a far larger province city is skipped.
 *
 * @param {number} currentTotal
 * @param {Region} region
 * @param {Province|null} province
 * @returns {BoltAmbition|null}
 */
export const getAmbition = (currentTotal: number, region: Region, province: Province | null): BoltAmbition | null => {
  const provinceCities = { beaten: [] as City[], upcoming: [] as City[] }
  const otherCities = { beaten: [] as City[], upcoming: [] as City[] }

  // Loop over provinces & cities, and place in correct constant for comparison
  for (const prov of PROVINCES_WITH_CITIES) {
    if (prov.region !== region) continue
    const group = prov.province === province ? provinceCities : otherCities
    for (const city of prov.cities) (city.population <= currentTotal ? group.beaten : group.upcoming).push(city)
  }

  // City sorting functions
  const byPopulationAsc = (a: City, b: City) => a.population - b.population
  const byPopulationDesc = (a: City, b: City) => b.population - a.population

  const beaten = [...provinceCities.beaten.sort(byPopulationDesc), ...otherCities.beaten.sort(byPopulationDesc)].slice(
    0,
    AMBITION_MAX_BEATEN_CITIES
  )
  const upcomingCities = [...provinceCities.upcoming.sort(byPopulationAsc), ...otherCities.upcoming.sort(byPopulationAsc)]

  if (!beaten.length || !upcomingCities.length) return null

  const goal = getAmbitionGoal(currentTotal)
  const nextGoal = getAmbitionGoal(goal)
  const preferredCities = provinceCities.upcoming.filter((city) => city.population < nextGoal)
  const goalCity = (preferredCities.length ? preferredCities : upcomingCities).reduce((closest, city) =>
    Math.abs(city.population - goal) < Math.abs(closest.population - goal) ? city : closest
  )
  const largerCities = upcomingCities.filter((city) => city.population > goalCity.population)

  return {
    goal,
    beaten: beaten.sort(byPopulationAsc),
    upcoming: [goalCity, ...largerCities.slice(0, AMBITION_CHART_CITIES - beaten.length - 1).sort(byPopulationAsc)]
  }
}
